import { isApiError } from "@sagot/api-client";
import { BASELINE_SIDE, WORKFLOW_DEFAULT_REASONS, type RestoreTarget } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Checkbox, Dialog, DialogContent, ErrorState, Icon, RadioGroup, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { DIFF_DRAFT, DIFF_LIVE } from "../p4-types";
import type { WorkflowDetail } from "../types";
import { useVersionDiff } from "../use-workflows-p4";
import { DiffBody } from "../versions/diff-body";
import { useDefaultCatalogue, useRestoreDefault } from "./use-defaults";

const R = "engage.workflows.defaults.restore";

/** What the dialog needs to know of the default it restores. */
export interface RestorableDefault {
  id: string;
  name: string;
  state: string;
  is_modified: boolean;
  has_default_update: boolean;
  has_unpublished_changes: boolean;
  system_key?: string | null;
}

interface RestoreDefaultDialogProps {
  workflow: RestorableDefault;
  /** The builder holds edits that are not saved: restoring replaces them too. */
  unsaved?: boolean;
  /** The server has put the default back; the answer is the workflow with its new draft. */
  onRestored: (workflow: WorkflowDetail, target: RestoreTarget) => void;
  onClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}

/** What the restore changes: the version that runs (or, when only the draft is replaced, the draft) next to the default. */
function Changes({ workflowId, publishing }: { workflowId: string; publishing: boolean }) {
  const { t } = useTranslation();
  const diff = useVersionDiff(workflowId, publishing ? DIFF_LIVE : DIFF_DRAFT, BASELINE_SIDE);
  if (diff.isError) return <ErrorState title={t("engage.workflows.versions.compareFailed")} onRetry={() => void diff.refetch()} retryLabel={t("common.retry")} />;
  if (diff.isPending) return <Skeleton className="h-40 rounded-md" aria-hidden="true" />;
  return <DiffBody data={diff.data} baseTitle={t(`${R}.yours`)} targetTitle={t(`${R}.original`)} identical={{ title: t(`${R}.nothing`), body: "" }} />;
}

/**
 * "Restore default": confirm, choose whether to publish at once, and see the diff between the workflow and the default it
 * started from. A newer default (`has_default_update`) can be chosen instead of the original; it is always published and
 * becomes the one Restore returns to. Changes that are not published are said to be replaced, then sent with `force`.
 */
export function RestoreDefaultDialog({ workflow, unsaved = false, onRestored, onClose, onCloseAutoFocus }: RestoreDefaultDialogProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const restore = useRestoreDefault(workflow.id);
  // The revision a newer default would move the workflow to is the catalogue's.
  const catalogue = useDefaultCatalogue(workflow.has_default_update);
  const newest = catalogue.data?.items.find((entry) => entry.system_key === workflow.system_key)?.revision;
  const [target, setTarget] = useState<RestoreTarget>("baseline");
  const [publish, setPublish] = useState(true);
  // A 409 from a draft that changed since the list was read: the warning appears and the next confirmation is the person's yes.
  const [staleDraft, setStaleDraft] = useState(false);
  const latest = target === "latest";
  const replacing = workflow.has_unpublished_changes || unsaved || staleDraft;
  const nothingToDo = !latest && !workflow.is_modified && !replacing;
  const publishing = latest || publish;
  const confirmLabel = latest ? t(`${R}.confirmLatest`) : publish ? t(`${R}.confirmPublish`) : t(`${R}.confirmDraft`);

  const submit = () => {
    restore.mutate({ publish: publishing, revision: target, force: replacing }, {
      onSuccess: (detail) => {
        const version = detail.live_version ?? 1;
        if (!publishing) toast.success(t(`${R}.restoredDraft`));
        else toast.success(t(latest ? `${R}.updated` : `${R}.restored`, { version }));
        onRestored(detail, target);
        onClose();
      },
      onError: (error) => {
        if (isApiError(error) && error.status === 409 && error.details.reason === WORKFLOW_DEFAULT_REASONS.unpublishedChanges) {
          setStaleDraft(true);
          return;
        }
        toast.error(message(error));
      },
    });
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${R}.title`, { name: workflow.name })}
        description={t(workflow.has_default_update ? `${R}.bodyUpdate` : `${R}.body`)}
        closeLabel={t("common.close")}
        className="max-w-4xl"
        onCloseAutoFocus={onCloseAutoFocus}
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>{t("common.cancel")}</Button>
            <Button loading={restore.isPending} disabled={nothingToDo} onClick={submit}>
              <Icon name="rotate-ccw" />
              {confirmLabel}
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {workflow.has_default_update ? (
            <RadioGroup
              aria-label={t(`${R}.target`)}
              value={target}
              onValueChange={setTarget}
              options={[
                { value: "baseline", label: t(`${R}.targetBaseline`), description: t(`${R}.targetBaselineHint`) },
                { value: "latest", label: newest === undefined ? t(`${R}.targetLatestPlain`) : t(`${R}.targetLatest`, { revision: newest }), description: t(`${R}.targetLatestHint`) },
              ]}
            />
          ) : null}
          {replacing ? (
            <p className="flex items-start gap-2 rounded-md bg-warning-soft p-3 text-sm" role="status" data-testid="restore-replaces">
              <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-warning" />
              {t(unsaved ? `${R}.unsaved` : `${R}.unpublished`)}
            </p>
          ) : null}
          <div>
            <Checkbox
              checked={publishing}
              disabled={latest}
              onCheckedChange={(checked) => { setPublish(checked === true); }}
              label={t(`${R}.publish`)}
              description={latest ? t(`${R}.publishLatest`) : t(`${R}.publishHint`)}
            />
            {workflow.state === "paused" ? <p className="mt-2 text-xs text-fg-subtle">{t(`${R}.wasPaused`)}</p> : null}
          </div>
          {latest ? null : (
            <section aria-labelledby="restore-changes" className="space-y-3">
              <h3 id="restore-changes" className="text-sm font-bold">{t(`${R}.changes`)}</h3>
              <Changes workflowId={workflow.id} publishing={publishing} />
            </section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
